import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../api/axios';
import { useAuth } from './AuthContext';
import { resolveMediaUrl } from '../lib/mediaUrl';

const DEFAULT_BRANDING = {
  domain: null,
  brand_name: 'Cantik',
  portal_name: 'Cantik Portal',
  primary_color: '#4f46e5',
  accent_color: '#a855f7',
  logo_url: '',
  support_email: 'support@cantiktech.com',
  tenant_id: null,
  tenant_options: [],
};

const BRANDING_CACHE_KEY = 'tenantBranding';
const BrandingContext = createContext(null);

const normalizeHex = (value, fallback) => {
  const hex = String(value || '').trim();
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex) ? hex : fallback;
};

const applyBranding = (branding) => {
  const root = document.documentElement;
  root.style.setProperty('--tenant-primary', branding.primary_color);
  root.style.setProperty('--tenant-accent', branding.accent_color);
};

export const BrandingProvider = ({ children }) => {
  const { user } = useAuth();
  const [branding, setBranding] = useState(() => {
    const stored = localStorage.getItem(BRANDING_CACHE_KEY);
    if (!stored) return DEFAULT_BRANDING;
    try {
      return { ...DEFAULT_BRANDING, ...JSON.parse(stored) };
    } catch {
      return DEFAULT_BRANDING;
    }
  });
  const [loading, setLoading] = useState(false);

  const applyAndStoreBranding = useCallback((nextBranding) => {
    const rawOptions = Array.isArray(nextBranding?.tenant_options) ? nextBranding.tenant_options : [];
    const tenant_options = rawOptions.map((opt) => ({
      ...opt,
      id: opt.id,
      name: opt.name,
      portal_name: opt.portal_name,
      primary_color: normalizeHex(opt?.primary_color, DEFAULT_BRANDING.primary_color),
      accent_color: normalizeHex(opt?.accent_color, DEFAULT_BRANDING.accent_color),
      // Keep relative paths; UI calls resolveMediaUrl once (avoids broken/double URLs for some tenants)
      logo_url: String(opt?.logo_url || '').trim(),
      support_email: opt.support_email || DEFAULT_BRANDING.support_email,
    }));
    const rawTenantId = nextBranding?.tenant_id;
    const parsedTenantId =
      rawTenantId != null && rawTenantId !== '' ? Number(rawTenantId) : Number.NaN;
    const tenant_id = Number.isFinite(parsedTenantId) && parsedTenantId > 0 ? parsedTenantId : null;
    const merged = {
      ...DEFAULT_BRANDING,
      ...nextBranding,
      tenant_id,
      tenant_options,
      primary_color: normalizeHex(nextBranding?.primary_color, DEFAULT_BRANDING.primary_color),
      accent_color: normalizeHex(nextBranding?.accent_color, DEFAULT_BRANDING.accent_color),
      logo_url: resolveMediaUrl(nextBranding?.logo_url || ''),
    };
    setBranding(merged);
    applyBranding(merged);
    localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(merged));
    return merged;
  }, []);

  const resolveBranding = useCallback(async ({ email, domain } = {}) => {
    const safeEmail = String(email || '').trim().toLowerCase();
    const safeDomain = String(domain || '').trim().toLowerCase();
    if (!safeEmail && !safeDomain) {
      return applyAndStoreBranding(DEFAULT_BRANDING);
    }

    setLoading(true);
    try {
      const response = await api.get('/domains/branding', {
        params: safeEmail ? { email: safeEmail } : { domain: safeDomain },
      });
      return applyAndStoreBranding(response.data?.data || DEFAULT_BRANDING);
    } catch (error) {
      return applyAndStoreBranding(DEFAULT_BRANDING);
    } finally {
      setLoading(false);
    }
  }, [applyAndStoreBranding]);

  useEffect(() => {
    applyBranding(branding);
  }, [branding]);

  useEffect(() => {
    const email = user?.email;
    const tenantBrandingFromSession = user?.tenant_id && (user?.tenant_logo_url || user?.tenant_primary_color || user?.tenant_accent_color)
      ? {
          domain: null,
          tenant_id: user.tenant_id,
          tenant_options: [],
          brand_name: user?.tenant_name || user?.company || DEFAULT_BRANDING.brand_name,
          portal_name: user?.tenant_portal_name || DEFAULT_BRANDING.portal_name,
          primary_color: user?.tenant_primary_color || DEFAULT_BRANDING.primary_color,
          accent_color: user?.tenant_accent_color || DEFAULT_BRANDING.accent_color,
          logo_url: user?.tenant_logo_url || '',
          support_email: user?.tenant_support_email || DEFAULT_BRANDING.support_email,
        }
      : null;

    if (tenantBrandingFromSession) {
      applyAndStoreBranding(tenantBrandingFromSession);
      return;
    }

    if (email) {
      resolveBranding({ email });
      return;
    }
    applyAndStoreBranding(DEFAULT_BRANDING);
  }, [
    user?.email,
    user?.company,
    user?.tenant_id,
    user?.tenant_name,
    user?.tenant_portal_name,
    user?.tenant_logo_url,
    user?.tenant_primary_color,
    user?.tenant_accent_color,
    user?.tenant_support_email,
    resolveBranding,
    applyAndStoreBranding
  ]);

  const value = useMemo(() => ({
    branding,
    loading,
    resolveBranding,
    resetBranding: () => applyAndStoreBranding(DEFAULT_BRANDING),
    logoSrc: branding.logo_url || null,
    brandDisplayName: branding.brand_name,
    portalName: branding.portal_name,
  }), [branding, loading, resolveBranding, applyAndStoreBranding]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};
